import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    borders, colors, fontSizes, fontWeights, lineHeights, space, radii, mediaQueries
} from 'style/config';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import Link from 'components/Link';
import localeUtils from 'utils/LanguageLocale';
import Empty from 'constants/empty';
import BundleWidgetEN from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_en_US';
import BundleWidgetFR from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(BundleWidgetEN, BundleWidgetFR);

const BundleProductCard = ({
    product, isCurrentProduct, isChecked, onCheckboxChange, onChangeSkuClick
}) => {
    const {
        brandName, variationType, productName, displayName, skuImages, listPrice, salePrice, variationValue
    } = product || Empty.Object;
    const imageUrl = skuImages?.imageUrl || skuImages?.['image250'];
    const cardProductName = isCurrentProduct ? displayName : productName;
    const allowSkuChange = !isCurrentProduct && variationType;
    const displayPrice = salePrice || listPrice;
    const originalPrice = salePrice && listPrice && salePrice !== listPrice ? listPrice : undefined;

    const renderChangeLink = css =>
        allowSkuChange && (
            <Link
                css={css}
                onClick={onChangeSkuClick}
            >
                {getText('change')} {variationType}
            </Link>
        );

    const renderPrice = (stackCss, priceCss) =>
        displayPrice && (
            <div css={stackCss}>
                <span css={[priceCss, originalPrice && styles.salePrice]}>{displayPrice}</span>
                {originalPrice && <span css={styles.originalPrice}>{originalPrice}</span>}
            </div>
        );

    return (
        <div css={styles.card}>
            <Checkbox
                checked={isCurrentProduct ? true : Boolean(isChecked)}
                onChange={isCurrentProduct ? () => {} : onCheckboxChange}
                aria-label={cardProductName}
                aria-disabled={isCurrentProduct || undefined}
                paddingY={0}
            />
            <div css={styles.contentWrapper}>
                {imageUrl && (
                    <img
                        src={imageUrl}
                        alt={cardProductName}
                        css={styles.image}
                    />
                )}
                <div css={styles.info}>
                    {brandName && <span css={styles.brand}>{brandName}</span>}
                    <span css={styles.productName}>{cardProductName}</span>
                    {variationValue && <span css={styles.variation}>{variationValue}</span>}
                    {isCurrentProduct && <span css={styles.badge}>{getText('currentProduct')}</span>}
                </div>
                <div css={styles.priceColumn}>
                    {renderPrice(styles.priceStack, styles.price)}
                    {renderChangeLink(styles.changeLinkMobile)}
                </div>
                <div css={styles.bottomSection}>
                    {isCurrentProduct && <span css={styles.badgeDesktop}>{getText('currentProduct')}</span>}
                    {renderChangeLink(styles.changeLinkDesktop)}
                    {renderPrice(styles.priceStackDesktop, styles.priceDesktop)}
                </div>
            </div>
        </div>
    );
};

const styles = {
    card: {
        display: 'flex',
        gap: 0,
        alignItems: 'center',
        padding: `${space[3]}px ${space[4]}px`,
        borderBottom: `${borders[1]} ${colors.lightGray}`,
        width: '100%',
        [mediaQueries.sm]: {
            gap: space[1],
            padding: 0,
            borderBottom: 'none',
            height: '100%'
        }
    },
    contentWrapper: {
        display: 'contents',
        [mediaQueries.sm]: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
            gap: space[2],
            alignSelf: 'stretch'
        }
    },
    image: {
        width: 60,
        height: 60,
        objectFit: 'cover',
        [mediaQueries.sm]: {
            width: 108,
            height: 108
        }
    },
    info: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flex: 1,
        [mediaQueries.sm]: {
            flex: 'none'
        }
    },
    priceColumn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        alignSelf: 'stretch',
        justifyContent: 'space-between',
        [mediaQueries.sm]: { display: 'none' }
    },
    bottomSection: {
        display: 'none',
        [mediaQueries.sm]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: space[4],
            marginTop: 'auto'
        }
    },
    brand: {
        fontSize: fontSizes.xs,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        [mediaQueries.sm]: {
            fontSize: fontSizes.sm
        }
    },
    productName: {
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight,
        whiteSpace: 'wrap',
        [mediaQueries.sm]: {
            fontSize: fontSizes.base
        }
    },
    variation: {
        fontSize: fontSizes.xs,
        lineHeight: lineHeights.tight,
        color: colors.gray
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        height: 18,
        backgroundColor: colors.nearWhite,
        borderRadius: radii[2],
        padding: `0 ${space[2]}px`,
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight,
        color: colors.gray,
        marginTop: space[2],
        [mediaQueries.sm]: { display: 'none' }
    },
    badgeDesktop: {
        display: 'none',
        [mediaQueries.sm]: {
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            height: 18,
            backgroundColor: colors.nearWhite,
            borderRadius: radii[2],
            padding: `0 ${space[2]}px`,
            fontSize: fontSizes.sm,
            lineHeight: lineHeights.tight,
            color: colors.gray
        }
    },
    price: {
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        textAlign: 'right'
    },
    priceDesktop: {
        display: 'none',
        [mediaQueries.sm]: {
            display: 'block',
            fontWeight: fontWeights.bold,
            lineHeight: lineHeights.tight
        }
    },
    priceStack: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end'
    },
    priceStackDesktop: {
        display: 'none',
        [mediaQueries.sm]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
        }
    },
    salePrice: {
        color: colors.red,
        fontWeight: fontWeights.bold
    },
    changeLinkMobile: {
        fontSize: fontSizes.sm,
        color: colors.link,
        textAlign: 'right',
        [mediaQueries.sm]: { display: 'none' }
    },
    changeLinkDesktop: {
        display: 'none',
        [mediaQueries.sm]: {
            display: 'block',
            color: colors.link
        }
    }
};

export default wrapFunctionalComponent(BundleProductCard, 'BundleProductCard');
